import { gameKeys, games } from "./games";
import type { GameKey, PerformanceGameSummary, PerformanceSummary, TicketHistoryRecord } from "./types";

function ticketCostFromHistory(item: TicketHistoryRecord): number {
  return Number(item.recommendation?.ticket_cost ?? games[item.game_key]?.ticketCost ?? 0);
}

function expectedReturnFromHistory(item: TicketHistoryRecord): number {
  const ticket = item.recommendation?.tickets?.find((candidate) => candidate.ticket.compact === item.compact) ?? item.recommendation?.tickets?.[0];
  if (!ticket) return 0;
  return Number(ticket.lower_ev ?? 0) + Number(ticket.jackpot_ev ?? 0);
}

function estimatedReturnFromHistory(item: TicketHistoryRecord): number {
  return Number(item.result?.estimated_prize_cash ?? 0);
}

function expectedAnyPrize(item: TicketHistoryRecord): number {
  return Number(item.recommendation?.any_prize_probability ?? item.recommendation?.portfolio_any_prize_probability_approx ?? 0);
}

function normalPrizeBand(expected: number, rows: number): { low: number; high: number } {
  const variance = Math.max(expected * Math.max(0, 1 - expected / Math.max(rows, 1)), 0);
  const margin = 2 * Math.sqrt(variance);
  return {
    low: Math.max(0, Math.floor(expected - margin)),
    high: Math.min(rows, Math.ceil(expected + margin))
  };
}

function bestLabel(item: TicketHistoryRecord): string | null {
  const result = item.result;
  if (!result) return null;
  const prize = result.prize_name ? String(result.prize_name) : "No prize";
  const whites = Number(result.white_hits ?? 0);
  const bonus = result.bonus_hit ? " + bonus" : "";
  return `${item.game_name}: ${prize} (${whites} white${bonus})`;
}

function resultStrength(item: TicketHistoryRecord): number {
  return Number(item.result?.estimated_prize_cash ?? 0) * 100 + Number(item.result?.white_hits ?? 0) + (item.result?.bonus_hit ? 0.5 : 0);
}

function summarizeGame(gameKey: GameKey, history: TicketHistoryRecord[]): PerformanceGameSummary {
  const gameHistory = history.filter((item) => item.game_key === gameKey);
  const checked = gameHistory.filter((item) => item.status === "draw_complete" && item.result);
  const best = checked.reduce<TicketHistoryRecord | null>((current, item) => {
    if (!current) return item;
    return resultStrength(item) > resultStrength(current) ? item : current;
  }, null);
  return {
    game_key: gameKey,
    game_name: games[gameKey].name,
    tracked_tickets: gameHistory.length,
    checked_tickets: checked.length,
    spent: gameHistory.reduce((sum, item) => sum + ticketCostFromHistory(item), 0),
    expected_return: checked.reduce((sum, item) => sum + expectedReturnFromHistory(item), 0),
    estimated_return: checked.reduce((sum, item) => sum + estimatedReturnFromHistory(item), 0),
    prize_tickets: checked.filter((item) => item.result?.prize_name).length,
    best_result: best ? bestLabel(best) : null
  };
}

export function buildPerformanceSummary(history: TicketHistoryRecord[]): PerformanceSummary {
  const played = history.filter((item) => item.status === "played" || item.status === "draw_complete");
  const checked = history.filter((item) => item.status === "draw_complete" && item.result);
  const checkedSpent = checked.reduce((sum, item) => sum + ticketCostFromHistory(item), 0);
  const expectedReturn = checked.reduce((sum, item) => sum + expectedReturnFromHistory(item), 0);
  const expectedPrizes = checked.reduce((sum, item) => sum + expectedAnyPrize(item), 0);
  const estimatedReturn = checked.reduce((sum, item) => sum + estimatedReturnFromHistory(item), 0);
  const best = checked.reduce<TicketHistoryRecord | null>((current, item) => {
    if (!current) return item;
    return resultStrength(item) > resultStrength(current) ? item : current;
  }, null);
  const prizeTickets = checked.filter((item) => item.result?.prize_name).length;
  const band = normalPrizeBand(expectedPrizes, checked.length);
  const sampleStatus =
    checked.length >= 100
      ? "Enough checked tickets to start judging calibration, still noisy."
      : checked.length > 0
        ? "Early record. Useful as an audit trail, not enough to judge skill."
        : "No completed draws checked yet.";

  return {
    tracked_tickets: history.length,
    played_tickets: played.length,
    checked_tickets: checked.length,
    prize_tickets: prizeTickets,
    total_spent: played.reduce((sum, item) => sum + ticketCostFromHistory(item), 0),
    checked_spent: checkedSpent,
    expected_return: expectedReturn,
    estimated_return: estimatedReturn,
    expected_profit: expectedReturn - checkedSpent,
    actual_profit: estimatedReturn - checkedSpent,
    total_white_hits: checked.reduce((sum, item) => sum + Number(item.result?.white_hits ?? 0), 0),
    bonus_hits: checked.filter((item) => item.result?.bonus_hit).length,
    expected_any_prizes: expectedPrizes,
    actual_prize_rate: checked.length ? prizeTickets / checked.length : 0,
    normal_low_prize_tickets: band.low,
    normal_high_prize_tickets: band.high,
    best_result: best ? bestLabel(best) : null,
    sample_status: sampleStatus,
    by_game: gameKeys.map((gameKey) => summarizeGame(gameKey, history))
  };
}
